import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { useAppSelector } from '../../../../redux/hooks';
import { lddGlobalSessionInfo } from '../../../navigationTypes';
import {
    deleteClientConnectEmailLogo,
    fetchClientConnectEmailLogo,
    uploadClientConnectEmailLogo
} from './emailLogoFuncs';

const ClientConnectEmailLogoCard = () => {
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((state) => state.SessionInfo);
    const sid = sessionInfo.sid;
    const province = sessionInfo.appProvinceShort;

    const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (!sid || !province) {
            setLoading(false);
            return;
        }
        setError(null);
        try {
            const data = await fetchClientConnectEmailLogo(province, sid);
            setPreviewUrl(data.hasLogo && data.url ? `${data.url}&t=${Date.now()}` : undefined);
        } catch {
            setError('Could not load logo status.');
            setPreviewUrl(undefined);
        } finally {
            setLoading(false);
        }
    }, [province, sid]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !sid || !province) {
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const res = await uploadClientConnectEmailLogo(province, sid, file);
            if (res.url) {
                setPreviewUrl(`${res.url}&t=${Date.now()}`);
            } else {
                await refresh();
            }
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ??
                err?.message ??
                'Upload failed.';
            setError(typeof msg === 'string' ? msg : 'Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const onRemove = async () => {
        if (!sid || !province) {
            return;
        }
        setUploading(true);
        setError(null);
        try {
            await deleteClientConnectEmailLogo(province, sid);
            setPreviewUrl(undefined);
        } catch {
            setError('Could not remove logo.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Card className="h-100" style={{ minWidth: '300px' }}>
            <Card.Header>
                <h2>Invitation email logo</h2>
            </Card.Header>
            <Card.Body>
                <p className="text-muted small mb-2">
                    Optional image shown at the top of ClientConnect invitation and ID reminder emails (PNG, JPEG,
                    GIF, or WebP, max 2 MB).
                </p>
                {loading ? (
                    <Spinner animation="border" size="sm" />
                ) : (
                    <>
                        {previewUrl ? (
                            <div className="mb-3">
                                <img
                                    src={previewUrl}
                                    alt="Firm logo preview"
                                    style={{ maxWidth: '240px', maxHeight: '120px', objectFit: 'contain' }}
                                />
                            </div>
                        ) : (
                            <p className="small text-muted mb-3">No custom logo — the default ClientConnect image is used.</p>
                        )}
                        <Form.Group className="mb-2">
                            <Form.Control
                                type="file"
                                accept="image/png,image/jpeg,image/gif,image/webp"
                                onChange={(ev) => void onFileChange(ev as React.ChangeEvent<HTMLInputElement>)}
                                disabled={uploading}
                            />
                        </Form.Group>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button variant="outline-danger" size="sm" disabled={uploading || !previewUrl} onClick={() => void onRemove()}>
                                Remove logo
                            </Button>
                        </div>
                        {error ? <p className="text-danger small mt-2 mb-0">{error}</p> : null}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default ClientConnectEmailLogoCard;
